import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Layout from './components/Layout';
import Home from './pages/Home';
import { LEGAL_PAGES } from './data/content';

/* Route-level code splitting. The 3D chunk only loads on routes that render a
   scene, so Investor Relations, Governance, Newsroom and Contact ship zero
   bytes of three.js. */
const TheGroup = lazy(() => import('./pages/TheGroup'));
const OurBusinesses = lazy(() => import('./pages/OurBusinesses'));
const Governance = lazy(() => import('./pages/Governance'));
const Sustainability = lazy(() => import('./pages/Sustainability'));
const Newsroom = lazy(() => import('./pages/Newsroom'));
const PressRelease = lazy(() => import('./pages/PressRelease'));
const Contact = lazy(() => import('./pages/Contact'));
const FishingIntelligence = lazy(() => import('./pages/FishingIntelligence'));
const Legal = lazy(() => import('./pages/Legal'));
const Search = lazy(() => import('./pages/Search'));
const NotFound = lazy(() => import('./pages/NotFound'));
const BrandPreview = lazy(() => import('./pages/BrandPreview'));

const IRLanding = lazy(() => import('./pages/investor/IRLanding'));
const Announcements = lazy(() => import('./pages/investor/Announcements'));
const AnnouncementDetail = lazy(() => import('./pages/investor/AnnouncementDetail'));
const FinancialResults = lazy(() => import('./pages/investor/FinancialResults'));
const AnnualReports = lazy(() => import('./pages/investor/AnnualReports'));
const ShareInformation = lazy(() => import('./pages/investor/ShareInformation'));
const GeneralMeetings = lazy(() => import('./pages/investor/GeneralMeetings'));
const CorporateActions = lazy(() => import('./pages/investor/CorporateActions'));
const VerifiedDocuments = lazy(() => import('./pages/investor/VerifiedDocuments'));
const EmailAlerts = lazy(() => import('./pages/investor/EmailAlerts'));
const IRContact = lazy(() => import('./pages/investor/IRContact'));

/* The Frac chunk is a named chunk of its own and is never prefetched or
   preloaded (no webpackPrefetch/webpackPreload hints), so its content does not
   appear in the public bundle. */
const Frac = lazy(() => import(/* webpackChunkName: "frac-private" */ './pages/Frac'));

const IS_PRODUCTION = process.env.NODE_ENV === 'production';

function RouteFallback() {
  return (
    <div className="u-container u-section" aria-busy="true" aria-live="polite">
      <p className="t-mono t-secondary">Loading…</p>
    </div>
  );
}

function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />

          <Route path="/investor-relations" element={<IRLanding />} />
          <Route path="/investor-relations/announcements" element={<Announcements />} />
          <Route path="/investor-relations/announcements/:slug" element={<AnnouncementDetail />} />
          <Route path="/investor-relations/financial-results" element={<FinancialResults />} />
          <Route path="/investor-relations/annual-reports" element={<AnnualReports />} />
          <Route path="/investor-relations/share-information" element={<ShareInformation />} />
          <Route path="/investor-relations/general-meetings" element={<GeneralMeetings />} />
          <Route path="/investor-relations/corporate-actions" element={<CorporateActions />} />
          <Route
            path="/investor-relations/verified-documents"
            element={<VerifiedDocuments />}
          />
          <Route path="/investor-relations/email-alerts" element={<EmailAlerts />} />
          <Route path="/investor-relations/contact" element={<IRContact />} />

          <Route path="/the-group" element={<TheGroup />} />
          <Route path="/our-businesses" element={<OurBusinesses />} />
          <Route path="/governance" element={<Governance />} />
          <Route path="/sustainability" element={<Sustainability />} />
          <Route path="/newsroom" element={<Newsroom />} />
          <Route path="/newsroom/:slug" element={<PressRelease />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/technology/intelligent-fishing-application" element={<FishingIntelligence />} />
          <Route path="/search" element={<Search />} />

          {LEGAL_PAGES.map((page) => (
            <Route key={page.slug} path={`/${page.slug}`} element={<Legal slug={page.slug} />} />
          ))}

          {/* Hidden. Redirects to / unless the server-side gate authorises it. */}
          <Route path="/frac" element={<Frac />} />

          {/* Dev only — excluded from production builds and from the sitemap. */}
          {IS_PRODUCTION ? null : <Route path="/_brand-preview" element={<BrandPreview />} />}
          {IS_PRODUCTION ? <Route path="/_brand-preview" element={<Navigate to="/" replace />} /> : null}

          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
